import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  apiRequest,
  ApiError,
  buildQuery,
  getStoredToken,
  toListResult,
  unwrapItem,
} from "../api";
import type { PaginationMeta, ListQuery, RoleResource } from "../types/roles";

export interface ResourceBucket<T = RoleResource> {
  items: T[];
  meta: PaginationMeta;
  loading: boolean;
  error: string | null;
  selected: T | null;
  loaded: boolean;
}

export interface RoleSliceState {
  buckets: Record<string, ResourceBucket>;
  submitting: boolean;
  success: string | null;
  error: string | null;
}

interface RoleTokenState {
  auth?: { access_token?: string | null };
  admin?: { access_token?: string | null };
}

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

interface FetchPayload {
  key: string;
  query?: ListQuery;
}

interface CreatePayload {
  key: string;
  resource?: string;
  path?: string;
  body?: unknown;
}

interface UpdatePayload {
  key: string;
  resource?: string;
  id: string | number;
  body?: unknown;
}

interface ActionPayload {
  key: string;
  resource?: string;
  id: string | number;
  suffix?: string;
  method?: HttpMethod;
  body?: unknown;
}

const emptyMeta: PaginationMeta = {
  currentPage: 1,
  lastPage: 1,
  perPage: 15,
  total: 0,
};

const makeBucket = (): ResourceBucket => ({
  items: [],
  meta: { ...emptyMeta },
  loading: false,
  error: null,
  selected: null,
  loaded: false,
});

const bucketFor = (state: RoleSliceState, key: string) => {
  if (!state.buckets[key]) state.buckets[key] = makeBucket();
  return state.buckets[key];
};

const errorMessage = (error: unknown) =>
  error instanceof ApiError || error instanceof Error
    ? error.message
    : "Something went wrong. Please try again.";

const resolveToken = (state: RoleTokenState) =>
  state.auth?.access_token ?? state.admin?.access_token ?? getStoredToken();

const normalizeSuffix = (suffix?: string) =>
  suffix ? `/${suffix.replace(/^\/+/, "")}` : "";

export const createRoleThunks = (
  role: string,
  endpoints: Record<string, string>,
  collectionKeys: Record<string, string[]> = {}
) => {
  const fetchResource = createAsyncThunk<
    { key: string; result: { items: RoleResource[]; meta: PaginationMeta } },
    FetchPayload,
    { state: RoleTokenState; rejectValue: string }
  >(`${role}/fetchResource`, async ({ key, query }, { getState, rejectWithValue }) => {
    const endpoint = endpoints[key];
    if (!endpoint) return rejectWithValue(`Unknown ${role} resource "${key}".`);

    try {
      const payload = await apiRequest<unknown>(`${endpoint}${buildQuery(query)}`, {
        token: resolveToken(getState()),
      });

      return {
        key,
        result: toListResult<RoleResource>(payload, collectionKeys[key] ?? []),
      };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  });

  const createResource = createAsyncThunk<
    { key: string; item: RoleResource | null },
    CreatePayload,
    { state: RoleTokenState; rejectValue: string }
  >(`${role}/createResource`, async ({ key, resource, path, body }, { getState, rejectWithValue }) => {
    const endpoint = path ?? endpoints[resource ?? key];
    if (!endpoint) return rejectWithValue(`Unknown ${role} resource "${key}".`);

    try {
      const payload = await apiRequest<unknown>(endpoint, {
        method: "POST",
        token: resolveToken(getState()),
        body,
      });

      return { key, item: unwrapItem<RoleResource>(payload) };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  });

  const updateResource = createAsyncThunk<
    { key: string; item: RoleResource | null },
    UpdatePayload,
    { state: RoleTokenState; rejectValue: string }
  >(`${role}/updateResource`, async ({ key, resource, id, body }, { getState, rejectWithValue }) => {
    const endpoint = endpoints[resource ?? key];
    if (!endpoint) return rejectWithValue(`Unknown ${role} resource "${key}".`);

    try {
      const payload = await apiRequest<unknown>(`${endpoint}/${id}`, {
        method: "PUT",
        token: resolveToken(getState()),
        body,
      });

      return { key, item: unwrapItem<RoleResource>(payload) };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  });

  const runAction = createAsyncThunk<
    { key: string; item: RoleResource | null },
    ActionPayload,
    { state: RoleTokenState; rejectValue: string }
  >(`${role}/runAction`, async (
    { key, resource, id, suffix, method = "POST", body },
    { getState, rejectWithValue }
  ) => {
    const endpoint = endpoints[resource ?? key];
    if (!endpoint) return rejectWithValue(`Unknown ${role} resource "${key}".`);

    try {
      const payload = await apiRequest<unknown>(
        `${endpoint}/${id}${normalizeSuffix(suffix)}`,
        {
          method,
          token: resolveToken(getState()),
          body,
        }
      );

      return { key, item: unwrapItem<RoleResource>(payload) };
    } catch (error) {
      return rejectWithValue(errorMessage(error));
    }
  });

  const initialState: RoleSliceState = {
    buckets: {},
    submitting: false,
    success: null,
    error: null,
  };

  const slice = createSlice({
    name: role,
    initialState,
    reducers: {
      selectResource(
        state,
        action: PayloadAction<{ key: string; item: RoleResource | null }>
      ) {
        bucketFor(state, action.payload.key).selected = action.payload.item;
      },
      patchBucket(
        state,
        action: PayloadAction<{ key: string; patch: Partial<ResourceBucket> }>
      ) {
        Object.assign(bucketFor(state, action.payload.key), action.payload.patch);
      },
      clearFeedback(state) {
        state.success = null;
        state.error = null;
      },
    },
    extraReducers: (builder) => {
      builder
        .addCase(fetchResource.pending, (state, action) => {
          const bucket = bucketFor(state, action.meta.arg.key);
          bucket.loading = true;
          bucket.error = null;
        })
        .addCase(fetchResource.fulfilled, (state, action) => {
          const bucket = bucketFor(state, action.payload.key);
          bucket.items = action.payload.result.items;
          bucket.meta = action.payload.result.meta;
          bucket.loading = false;
          bucket.loaded = true;
          bucket.error = null;
        })
        .addCase(fetchResource.rejected, (state, action) => {
          const bucket = bucketFor(state, action.meta.arg.key);
          bucket.loading = false;
          bucket.loaded = true;
          bucket.error = action.payload ?? action.error.message ?? "Unable to load records.";
        })

        .addCase(createResource.pending, (state) => {
          state.submitting = true;
          state.success = null;
          state.error = null;
        })
        .addCase(createResource.fulfilled, (state, action) => {
          state.submitting = false;
          state.success = "Record created successfully.";
          const bucket = bucketFor(state, action.payload.key);
          if (action.payload.item) {
            bucket.items = [action.payload.item, ...bucket.items];
            bucket.meta.total += 1;
          }
        })
        .addCase(createResource.rejected, (state, action) => {
          state.submitting = false;
          state.error = action.payload ?? action.error.message ?? "Unable to create record.";
        })

        .addCase(updateResource.pending, (state) => {
          state.submitting = true;
          state.success = null;
          state.error = null;
        })
        .addCase(updateResource.fulfilled, (state, action) => {
          state.submitting = false;
          state.success = "Record updated successfully.";
          if (action.payload.item) {
            const bucket = bucketFor(state, action.payload.key);
            bucket.items = bucket.items.map((item) =>
              String(item.id) === String(action.payload.item?.id)
                ? (action.payload.item as RoleResource)
                : item
            );
          }
        })
        .addCase(updateResource.rejected, (state, action) => {
          state.submitting = false;
          state.error = action.payload ?? action.error.message ?? "Unable to update record.";
        })

        .addCase(runAction.pending, (state) => {
          state.submitting = true;
          state.success = null;
          state.error = null;
        })
        .addCase(runAction.fulfilled, (state, action) => {
          state.submitting = false;
          state.success = "Action completed successfully.";
          if (action.payload.item) {
            const bucket = bucketFor(state, action.payload.key);
            bucket.items = bucket.items.map((item) =>
              String(item.id) === String(action.payload.item?.id)
                ? (action.payload.item as RoleResource)
                : item
            );
          }
        })
        .addCase(runAction.rejected, (state, action) => {
          state.submitting = false;
          state.error = action.payload ?? action.error.message ?? "Unable to complete action.";
        });
    },
  });

  return {
    reducer: slice.reducer,
    actions: slice.actions,
    fetchResource,
    createResource,
    updateResource,
    runAction,
  };
};

export type RoleThunks = ReturnType<typeof createRoleThunks>;
export type RoleActions = RoleThunks["actions"];
